import React from "react";
import { Link, useNavigate, useRouteLoaderData } from "react-router-dom";
import {
  userDelete,
  userTextLongDescription,
  userTextShortDescription,
  userView,
} from "../../../../api/user";
import LabeledEntry from "../../../helpers/LabeledEntry";
import { userEmailListByUser } from "../../../../api/useremail";
import { Button, Stack, styled } from "@mui/material";
import UserPrivilegeDisplay from "./UserPrivilegeDisplay";
import { testSessionLoader } from "../../../../loaders/helpers";
import { getSessionUserEntityId } from "../../../../bandung";
import useInfoSnackbar from "../../../../hooks/useInfoSnackbar";
import UserSecondaryEmailsDisplay from "./UserSecondaryEmailsDisplay";
import UserImageDisplay from "./UserImageDisplay";
import ConfirmationContainer from "../../../helpers/ConfirmationContainer";

const StyledUserImageContainer = styled(Stack)`
  justify-content: space-between;
  width: 100%;
  gap: 2em;
`;

const StyledEntryContainer = styled(Stack)`
  gap: 1em;
  width: 80%;
  padding: 2em 0 4em;
`;

const StyledButtonContainer = styled(Stack)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4em;
`;

const userLoader = async (params) => {
  await testSessionLoader(params);
  const userEntityId = params.params.id;
  const userListRes = await userView(userEntityId);
  const user = userListRes.data;
  const shortDescRes = await userTextShortDescription(user.userEntityId);
  const shortDescription = shortDescRes.data;
  const longDescRes = await userTextLongDescription(user.userEntityId);
  const userEmailsRes = await userEmailListByUser(user.userEntityId);
  const longDescription = longDescRes.data;
  const primaryEmail = userEmailsRes.data.find(
    email => email.userEmailEntityId === user.userEmailEntityId);
  const secondaryEmails = userEmailsRes.data.filter(
    email => email.userEmailEntityId !== primaryEmail.userEmailEntityId);
  return {
    user,
    shortDescription,
    longDescription,
    primaryEmail,
    secondaryEmails,
  };
};

const User = () => {
  const {
    user,
    shortDescription,
    longDescription,
    primaryEmail,
    secondaryEmails,
  } = useRouteLoaderData("adminUser");
  const infoSnackbar = useInfoSnackbar();
  const navigate = useNavigate();

  const handleDeleteConfirmed = async () => {
    const res = await userDelete(user.userEntityId);
    if (res.code === "SUCCESS") {
      infoSnackbar.alertSuccess(res.message);
      navigate("../", { replace: true });
    } else {
      infoSnackbar.alertError(res.message);
    }
  };

  const canDeleteUser = user.userEntityId !== getSessionUserEntityId();
  const placeholder = "Not Specified";

  return (
    <>
      <UserPrivilegeDisplay user={user}/>
      <StyledEntryContainer>
        <StyledUserImageContainer direction={{ sm: "row" }}>
          <Stack
            gap="1em"
            order={{ xs: 1, sm: 0 }}
          >
            <LabeledEntry
              label="Title"
              value={user.title}
              placeholder={placeholder}
            />
            <LabeledEntry
              label="First Name"
              value={user.firstName}
              placeholder={placeholder}
            />
            <LabeledEntry
              label="Last Name"
              value={user.lastName}
              placeholder={placeholder}
            />
          </Stack>
          <UserImageDisplay user={user}/>
        </StyledUserImageContainer>
        <LabeledEntry
          label="Primary Email"
          value={primaryEmail.email}
          placeholder={placeholder}
          link={`../../emails/${primaryEmail.userEmailEntityId}`}
        />
        {!!secondaryEmails.length &&
          <UserSecondaryEmailsDisplay secondaryEmails={secondaryEmails}/>}
        <LabeledEntry
          label="Create Date"
          value={user.createDate}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Last Modified"
          value={user.modifyDate}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Phone Number"
          value={user.phone}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Timezone"
          value={user.timezone}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Short Description"
          value={shortDescription}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Long Description"
          value={longDescription}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Url"
          value={user.url}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Linkedin User ID"
          value={user.linkedinUserId}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Facebook User ID"
          value={user.facebookUserId}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Gmail User ID"
          value={user.gmailUserId}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Skype User ID"
          value={user.skypeUserId}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Address1"
          value={user.address1}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Address2"
          value={user.address2}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="City"
          value={user.city}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="State"
          value={user.state}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Postal Code"
          value={user.postal}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Quit Date"
          value={user.quitDate}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Show Order"
          value={user.showOrder}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Reminders"
          value={user.reminders}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="Use HTML"
          value={user.useHtml ?? false}
          placeholder={placeholder}
        />
        <LabeledEntry
          label="User Entity ID"
          value={user.userEntityId}
          placeholder={placeholder}
        />
      </StyledEntryContainer>
      <StyledButtonContainer>
        <Button
          component={Link}
          to="update"
          variant="contained"
        >
          Update
        </Button>
        {canDeleteUser &&
          <ConfirmationContainer
            dialogTitle="Confirm User Deletion"
            dialogMessage={`Are you sure that you want to delete user "${user.firstName} ${user.lastName}" at "${primaryEmail.email}"?`}
            onConfirmed={handleDeleteConfirmed}
          >
            <Button
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </ConfirmationContainer>}
      </StyledButtonContainer>
    </>
  );
};

export { userLoader };
export default User;